import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TeamDashboardResult } from '../types';
import { fetchMyTeam, downloadReport } from '../api/training';

export default function TeamDashboard() {
  const { t } = useTranslation();
  const [data, setData] = useState<TeamDashboardResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchMyTeam()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="flex items-center justify-center py-12" role="status"><div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-brand-500" /></div>;
  if (error) return <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">{error}</div>;
  if (!data || data.team_size === 0) return <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center text-sm text-gray-500">{t('team.noReports', 'No direct reports found.')}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('team.title', 'My Team')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('team.subtitle', 'Development overview for your direct reports.')}</p>
        </div>
        <button type="button" onClick={() => downloadReport(`team/${encodeURIComponent('me')}`)} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
          {t('team.exportCsv', 'Export CSV')}
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="card"><p className="text-sm text-gray-500">{t('team.teamSize', 'Team Size')}</p><p className="mt-1 text-2xl font-bold text-gray-900">{data.team_size}</p></div>
        <div className="card"><p className="text-sm text-gray-500">{t('team.activeGoals', 'Active Goals')}</p><p className="mt-1 text-2xl font-bold text-blue-600">{data.active_goals}</p></div>
        <div className="card"><p className="text-sm text-gray-500">{t('team.completedGoals', 'Completed')}</p><p className="mt-1 text-2xl font-bold text-green-600">{data.completed_goals}</p></div>
        <div className="card"><p className="text-sm text-gray-500">{t('team.overdueGoals', 'Overdue')}</p><p className="mt-1 text-2xl font-bold text-red-600">{data.overdue_goals}</p></div>
      </div>

      {/* Team skill averages */}
      {Object.keys(data.team_skill_averages).length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('team.skillAverages', 'Team Skill Averages')}</h2>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {Object.entries(data.team_skill_averages).sort(([,a], [,b]) => b - a).map(([cat, avg]) => (
              <div key={cat} className="rounded-lg border border-gray-200 bg-white p-3">
                <p className="text-xs text-gray-500 truncate" title={cat}>{cat}</p>
                <p className={['text-lg font-bold', avg >= 4 ? 'text-green-600' : avg >= 3 ? 'text-blue-600' : avg >= 2 ? 'text-amber-600' : 'text-red-600'].join(' ')}>{avg.toFixed(1)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Team members table */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('team.members', 'Team Members')}</h2>
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500">{t('team.name', 'Name')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">{t('team.role', 'Role')}</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">{t('team.skills', 'Skills')}</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">{t('team.avgProf', 'Avg Level')}</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">{t('team.active', 'Active')}</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">{t('team.completed', 'Done')}</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">{t('team.overdue', 'Overdue')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {data.members.map((m) => (
                <tr key={m.employee_id} className={m.overdue_goals > 0 ? 'bg-red-50' : ''}>
                  <td className="px-4 py-3 font-medium text-gray-900">{m.name}</td>
                  <td className="px-4 py-3 text-gray-500">{m.job_title ?? '\u2014'}</td>
                  <td className="px-4 py-3 text-right text-gray-700">{m.skill_count}</td>
                  <td className="px-4 py-3 text-right font-semibold text-gray-700">{m.avg_proficiency.toFixed(1)}</td>
                  <td className="px-4 py-3 text-right text-blue-600">{m.active_goals}</td>
                  <td className="px-4 py-3 text-right text-green-600">{m.completed_goals}</td>
                  <td className="px-4 py-3 text-right text-red-600">{m.overdue_goals}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
